// TODO: Replace remaining TextInput with form field components from @/components/form
import { zodResolver } from '@hookform/resolvers/zod'
import { UploadSimpleIcon } from '@phosphor-icons/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useCreateCommunityEssential } from '@/api/community-essentials/create-community-essential'
import { useDeleteCommunityEssential } from '@/api/community-essentials/delete-community-essential'
import { useGetAllFolders } from '@/api/community-essentials/get-all-folders'
import { useUpdateCommunityEssential } from '@/api/community-essentials/update-community-essential'
import { SelectFormField } from '@/components/form/select-form-field'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { TextInput } from '@/components/ui/text-input'
import { ZoomFrame } from '@/components/ui/zoomable-image'
import { API_ERRORS } from '@/constants'
import { toastFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import type { CommunityEssentialModel } from '@/models'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/error'

import {
  EssentialFormData,
  getFileSizeErrorMessage,
  ICON_OPTIONS,
  INPUT_TYPE_OPTIONS,
  MAX_FILE_SIZE,
  makeEssentialFormSchema,
  START_POSITION,
} from '../community-essentials.constants'
import type { EssentialIcon, EssentialType } from '../community-essentials.types'
import { buildFolderTreeOptions } from '../community-essentials.utils'
import * as S from './essential-form-modal.styled'

interface EssentialFormProps {
  buildingId: number
  currentFolderId: number | null
  essentialToEdit: CommunityEssentialModel | null
  onClose: () => void
}

export const EssentialFormModal = ({
  buildingId,
  currentFolderId,
  essentialToEdit,
  onClose,
}: EssentialFormProps) => {
  const { t } = useTranslation(['community-essentials', 'common'])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)

  const { mutateAsync: createEssential } = useCreateCommunityEssential()
  const { mutateAsync: updateEssential } = useUpdateCommunityEssential()
  const { isPending: isDeleting, mutateAsync: deleteEssential } = useDeleteCommunityEssential()
  const { data: allFolders } = useGetAllFolders({ buildingId })

  const folderOptions = useMemo(() => buildFolderTreeOptions({ allFolders, t }), [allFolders, t])

  const [fileObjectUrl, setFileObjectUrl] = useState<string | null>(null)

  const form = useForm<EssentialFormData>({
    defaultValues: {
      folderId: essentialToEdit?.folderId ?? currentFolderId ?? 0,
      icon: essentialToEdit?.icon ?? ('' as EssentialIcon),
      name: essentialToEdit?.name ?? '',
      type: essentialToEdit?.type ?? ('' as EssentialType),
      value: essentialToEdit?.value ?? '',
    },
    mode: 'onChange',
    resolver: zodResolver(makeEssentialFormSchema(t)),
  })

  const {
    formState: { errors, isDirty, isSubmitting, isValid, touchedFields },
    register,
  } = form

  const handleSubmit = async (values: EssentialFormData) => {
    try {
      const payload = {
        buildingId,
        folderId: values.folderId === 0 ? null : values.folderId,
        icon: values.icon,
        name: values.name,
        type: values.type,
        value: values.value,
        ...(file && ['pdf', 'image'].includes(values.type) ? { file } : {}),
      }

      if (essentialToEdit) {
        await updateEssential({ id: essentialToEdit.id, ...payload })
      } else {
        // NOTE: create's FormData payload omits both `null` and `undefined` values equally,
        // so normalizing `null` to `undefined` here has no effect on the request sent.
        await createEssential({
          position: START_POSITION,
          ...payload,
          folderId: payload.folderId ?? undefined,
        })
      }

      onClose()
    } catch (error) {
      const apiError = error as Error
      toastFactory.error(
        getApiErrorStatus(apiError) === API_ERRORS.PAYLOAD_TOO_LARGE
          ? getFileSizeErrorMessage(t)
          : getApiErrorMessage(apiError),
      )
    }
  }

  const handleDelete = async () => {
    if (!essentialToEdit) return
    try {
      await deleteEssential({ buildingId, id: essentialToEdit.id })
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error as Error))
    }
  }

  const typeValue = useWatch({
    control: form.control,
    name: 'type',
  })

  useEffect(() => {
    setFile(null)
    setFileObjectUrl(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [typeValue])

  const isPdfOrImage = typeValue === 'pdf' || typeValue === 'image'
  const isFileSelected = isPdfOrImage && file !== null
  const typeChanged = essentialToEdit && typeValue !== essentialToEdit.type
  const isFileRequired = isPdfOrImage && (!essentialToEdit || typeChanged)

  const existingFileUrl =
    essentialToEdit &&
    typeValue === essentialToEdit.type &&
    ['pdf', 'image'].includes(essentialToEdit.type)
      ? getAssetLink({ file: essentialToEdit.value, path: 'chitown-building' })
      : null
  const previewUrl = fileObjectUrl ?? existingFileUrl

  const disableSubmit = isPdfOrImage
    ? (isFileRequired && !isFileSelected) || isSubmitting
    : !isValid || !isDirty || isSubmitting

  return (
    <FormProvider {...form}>
      <Modal
        size="small"
        title={
          essentialToEdit
            ? t('community-essentials:essentialFormModal.editTitle')
            : t('community-essentials:essentialFormModal.addTitle')
        }
        footer={
          <>
            {essentialToEdit && (
              <Button loading={isDeleting} use="secondary" size="medium" onClick={handleDelete}>
                {t('common:actions.delete')}
              </Button>
            )}
            <Button use="secondary" size="medium" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              disabled={disableSubmit}
              loading={isSubmitting}
              size="medium"
              onClick={form.handleSubmit(handleSubmit)}
            >
              {essentialToEdit
                ? t('community-essentials:update')
                : t('community-essentials:essentialFormModal.addButton')}
            </Button>
          </>
        }
        onClose={onClose}
      >
        <S.Fields>
          <TextInput
            error={touchedFields.name && errors.name ? errors.name.message : undefined}
            label={t('community-essentials:essentialFormModal.name.label')}
            maxLength={100}
            placeholder={t('community-essentials:essentialFormModal.name.placeholder')}
            {...register('name')}
          />

          <SelectFormField
            label={t('community-essentials:essentialFormModal.inputType.label')}
            name="type"
            options={INPUT_TYPE_OPTIONS}
            placeholder={t('community-essentials:essentialFormModal.inputType.placeholder')}
          />

          <SelectFormField
            label={t('community-essentials:essentialFormModal.icon.label')}
            name="icon"
            options={ICON_OPTIONS}
            placeholder={t('community-essentials:essentialFormModal.icon.placeholder')}
          />

          {!isPdfOrImage && (
            <TextInput
              disabled={!typeValue}
              error={touchedFields.value && errors.value ? errors.value.message : undefined}
              label={t('community-essentials:essentialFormModal.value.label')}
              maxLength={2000}
              placeholder={
                typeValue
                  ? t('community-essentials:essentialFormModal.value.placeholder', {
                      type: typeValue,
                    })
                  : t('community-essentials:essentialFormModal.value.placeholderEmpty')
              }
              {...register('value')}
            />
          )}

          {isPdfOrImage && (
            <div>
              <input
                ref={fileInputRef}
                hidden
                accept={typeValue === 'pdf' ? '.pdf' : 'image/*'}
                type="file"
                onChange={(e) => {
                  const selected = e.target.files?.[0] ?? null
                  if (selected && selected.size > MAX_FILE_SIZE) {
                    toastFactory.error(getFileSizeErrorMessage(t))
                    e.target.value = ''
                    return
                  }
                  setFile(selected)
                  setFileObjectUrl(selected ? URL.createObjectURL(selected) : null)
                }}
              />
              <Button size="medium" onClick={() => fileInputRef.current?.click()}>
                <Icon icon={UploadSimpleIcon} size="1.6rem" />
                {typeValue === 'pdf'
                  ? t('community-essentials:essentialFormModal.attachPdfButton')
                  : t('community-essentials:essentialFormModal.attachImageButton')}
              </Button>
              {previewUrl && (
                <S.FilePreview>
                  {typeValue === 'pdf' ? (
                    <iframe
                      src={previewUrl}
                      title={t('community-essentials:essentialFormModal.pdfPreviewTitle')}
                    />
                  ) : (
                    <ZoomFrame
                      fill
                      src={previewUrl}
                      title={t('community-essentials:essentialFormModal.previewAlt')}
                    >
                      <img
                        alt={t('community-essentials:essentialFormModal.previewAlt')}
                        src={previewUrl}
                      />
                    </ZoomFrame>
                  )}
                </S.FilePreview>
              )}
            </div>
          )}

          <SelectFormField
            searchable
            type="nested"
            label={t('community-essentials:essentialFormModal.folder.label')}
            name="folderId"
            options={folderOptions}
            placeholder={t('community-essentials:essentialFormModal.folder.placeholder')}
          />
        </S.Fields>
      </Modal>
    </FormProvider>
  )
}
